import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { apiClient } from '../services/apiClient';
import {
  getToken,
  getRefreshToken,
  setLocalSession,
  updateActiveToken,
  clearToken,
  isImpersonating,
} from '../services/authToken';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;
const AuthContext = createContext();

// Re-export so existing callers of `import { apiClient } from '../contexts/AuthContext'`
// continue to work without changes.
export { apiClient };

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => getToken());
  const [loading, setLoading] = useState(true);

  // Track in-flight refresh so concurrent 401s don't fire multiple refreshes.
  const refreshPromiseRef = useRef(null);
  // Keep a stable reference to the current token for the interceptor closure.
  const tokenRef = useRef(token);

  useEffect(() => {
    tokenRef.current = token;
  }, [token]);

  const authHeaders = useCallback(() => {
    if (token) return { Authorization: `Bearer ${token}` };
    return {};
  }, [token]);

  // ── Internal helpers ──────────────────────────────────────────────────────

  // Normal (kalici) oturum: localStorage. Form girisi / register / google buraya gelir.
  const _applySession = (newToken, newRefresh, userData) => {
    setLocalSession(newToken, newRefresh);
    tokenRef.current = newToken;
    setToken(newToken);
    if (userData) setUser(userData);
  };

  const _clearSession = () => {
    clearToken();
    tokenRef.current = null;
    setToken(null);
    setUser(null);
  };

  const _doRefresh = useCallback(async () => {
    const storedRefresh = getRefreshToken();
    if (!storedRefresh) throw new Error('no_refresh_token');
    // Use plain axios (not apiClient) to avoid triggering the same interceptor.
    const res = await axios.post(`${API}/auth/refresh`, { refresh_token: storedRefresh });
    const { token: newToken, refresh_token: newRefresh } = res.data;
    // Yeni token'i aktif oturum kapsamina yaz (impersonation sekmesiyse sessionStorage).
    updateActiveToken(newToken, newRefresh);
    tokenRef.current = newToken;
    setToken(newToken);
    return newToken;
  }, []);

  // ── Axios interceptors ────────────────────────────────────────────────────

  useEffect(() => {
    // Attach the latest token to outgoing requests aimed at our own API.
    const attachAuthHeader = (config) => {
      const t = tokenRef.current;
      if (!t) return config;
      // Only attach when the caller hasn't already set an Authorization header,
      // and only for requests to our own API so we never leak the token to
      // third-party URLs from the global axios instance.
      const hasAuth = config.headers?.Authorization || config.headers?.authorization;
      const url = config.url || '';
      const isOurApi =
        url.startsWith('/') ||
        (API && url.startsWith(API)) ||
        (BACKEND_URL && url.startsWith(`${BACKEND_URL}/`));
      if (!hasAuth && isOurApi) {
        config.headers = config.headers || {};
        config.headers['Authorization'] = `Bearer ${t}`;
      }
      return config;
    };

    // Shared 401 handler: try refresh once, retry the original request via the
    // SAME instance it came from; if refresh fails, clear session + redirect.
    const makeErrorHandler = (instance) => async (error) => {
      const originalRequest = error.config;
      const status = error.response?.status;

      if (
        status === 401 &&
        originalRequest &&
        !originalRequest._retry &&
        !originalRequest.url?.includes('/auth/refresh') &&
        !originalRequest.url?.includes('/auth/login')
      ) {
        originalRequest._retry = true;

        try {
          // Deduplicate concurrent 401s into one refresh call.
          if (!refreshPromiseRef.current) {
            refreshPromiseRef.current = _doRefresh().finally(() => {
              refreshPromiseRef.current = null;
            });
          }
          const newToken = await refreshPromiseRef.current;
          originalRequest.headers = originalRequest.headers || {};
          originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
          return instance(originalRequest);
        } catch {
          // Refresh failed - log the user out and redirect to login,
          // preserving the current location as a return-to target.
          _clearSession();
          if (window.location.pathname !== '/login') {
            const returnTo = encodeURIComponent(
              window.location.pathname + window.location.search
            );
            window.location.replace(`/login?returnTo=${returnTo}`);
          }
          return Promise.reject(error);
        }
      }

      return Promise.reject(error);
    };

    // apiClient instance.
    const apiReqId = apiClient.interceptors.request.use(attachAuthHeader);
    const apiResId = apiClient.interceptors.response.use(
      (response) => response,
      makeErrorHandler(apiClient)
    );

    // Global axios default instance (covers the ~90 raw axios.* call sites such
    // as LessonPage/ExamPage that do not go through apiClient). Without this,
    // an expired session on those pages showed a stuck retry screen instead of
    // redirecting to /login.
    const axiosReqId = axios.interceptors.request.use(attachAuthHeader);
    const axiosResId = axios.interceptors.response.use(
      (response) => response,
      makeErrorHandler(axios)
    );

    return () => {
      apiClient.interceptors.request.eject(apiReqId);
      apiClient.interceptors.response.eject(apiResId);
      axios.interceptors.request.eject(axiosReqId);
      axios.interceptors.response.eject(axiosResId);
    };
  }, [_doRefresh]);

  // ── Auth check on mount ───────────────────────────────────────────────────

  const checkAuth = useCallback(async () => {
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    const storedToken = getToken();
    if (!storedToken) {
      setLoading(false);
      return;
    }

    try {
      const res = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${storedToken}` },
        withCredentials: true,
      });
      setUser(res.data);
      setToken(storedToken);
      tokenRef.current = storedToken;
    } catch (err) {
      if (err.response?.status === 401) {
        // Try refresh before giving up.
        try {
          await _doRefresh();
          const res2 = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${tokenRef.current}` },
            withCredentials: true,
          });
          setUser(res2.data);
        } catch {
          _clearSession();
        }
      } else {
        _clearSession();
      }
    } finally {
      setLoading(false);
    }
  }, [_doRefresh]);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  // ── Cross-tab / cross-session token degisimi izleme ────────────────────────
  // Ayni origin'de tek `auth_token` oldugundan, baska bir sekmedeki giris/cikis
  // (veya impersonation yeni sekmesi) bu sekmenin token'ini sessizce degistirebilir.
  // Sessiz "kirik" durum (UI eski rolle render olup API'den yetki hatasi almasi)
  // olusmasin diye degisimi yakalayip kullaniciyi yeniden yuklemeye zorluyoruz.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key !== 'auth_token') return;
      // Impersonation sekmesi kendi token'ini sessionStorage'dan okur; baska
      // sekmedeki localStorage degisiminden ETKILENMEMELI (admin gezinince bu
      // sekme reload OLMAZ). 'storage' event'i yalnizca DIGER sekmelerden gelir.
      if (isImpersonating()) return;
      const current = tokenRef.current;
      // Token gercekten degistiyse (baska sekme login/logout/impersonate yapti)
      if ((e.newValue || null) !== (current || null)) {
        // Tam reload: AuthContext yeni token ile bastan auth eder, eski/yanlis
        // rolle render edilmis ekranlar (ornn. admin paneli) temizlenir.
        window.location.reload();
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  // ── Public auth actions ───────────────────────────────────────────────────

  const loginWithCredentials = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    const { token: newToken, refresh_token: newRefresh, user: userData, must_change_password } = res.data;
    _applySession(newToken, newRefresh, userData);
    // Attach the flag so LoginPage / callers can act on it
    if (userData && must_change_password) userData.must_change_password = true;
    return userData;
  };

  // Web self-register kapali (bkz. backend SELF_REGISTER_AKTIF, varsayilan
  // false -> POST /auth/register 403 self_register_disabled doner). Fonksiyon
  // artik UI'dan cagrilmiyor (RegisterPage route'tan cikarildi), bayrak geri
  // acilirsa diye silinmedi.
  const register = async (name, email, password) => {
    const res = await axios.post(`${API}/auth/register`, { name, email, password });
    const { token: newToken, refresh_token: newRefresh, user: userData } = res.data;
    _applySession(newToken, newRefresh, userData);
    return userData;
  };

  const loginWithGoogle = () => {
    return { disabled: true, reason: 'google_oauth_disabled' };
  };

  const processGoogleSession = async (sessionId) => {
    const res = await axios.post(
      `${API}/auth/google-session`,
      { session_id: sessionId },
      { withCredentials: true }
    );
    const { token: newToken, refresh_token: newRefresh, user: userData } = res.data;
    _applySession(newToken, newRefresh, userData);
    return userData;
  };

  const logout = async () => {
    const storedRefresh = getRefreshToken();
    try {
      await axios.post(
        `${API}/auth/logout`,
        { refresh_token: storedRefresh || null },
        { headers: authHeaders(), withCredentials: true }
      );
    } catch { /* ignore */ }
    _clearSession();
  };

  return (
    <AuthContext.Provider value={{
      user, token, loading, authHeaders,
      loginWithCredentials, register, loginWithGoogle,
      processGoogleSession, logout, setUser,
      isImpersonating: isImpersonating(),
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
